import { BEIGE_300, BEIGE_700, BLUE_600, MOBILE_VIEW_WIDTH_QUERY } from 'constants/styling/theme'
import { Box, Stack, useMediaQuery } from '@mui/material'
import { DynamicFlowStepper, DynamicFlowView, useDynamicFlowController } from 'components/common/DynamicFlow'
import { useEffect, useLayoutEffect } from 'react'

import { Path } from 'constants/router'
import { SignupCreateAccountStep } from './SignupCreateAccountStep'
import { SignupCreateWorkspaceStep } from './SignupCreateWorkspaceStep'
import { useSignupRegistration } from './SignupRegistration.context'
import { useTranslation } from 'react-i18next'
import { useUserMeAlternative } from 'dataQueries'

/** Enum of all registration steps */
export enum RegistrationStepKey {
  CREATE_ACCOUNT = 'CREATE_ACCOUNT',
  CREATE_WORKSPACE = 'CREATE_WORKSPACE',
}

export const SignupRegistrationController: React.FC = (props: any) => {
  const { t } = useTranslation(['signup', 'country', 'language', 'common'])
  const flowCtrl = useDynamicFlowController()
  const isMobileView = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)

  const { createUser } = useSignupRegistration()

  // Poll /user/me only after the form is submitted (POST /user/ fired) — the freshly created
  // platform user might not be resolvable immediately (auth/BE propagation delay). Polling
  // before submit is harmful: the BE can transiently answer 2xx for a not-yet-provisioned
  // user, and a premature "success" would redirect away and rip the form from under the
  // user while they are still filling it (ISSUE-2266)
  const isUserCreated = createUser.isSuccess
  const { data: provisionedUser, isSuccess, refetch } = useUserMeAlternative(isUserCreated)

  useEffect(() => {

    flowCtrl.createEntryNode(
      RegistrationStepKey.CREATE_ACCOUNT,
      {
        component: <SignupCreateAccountStep />,
        hasFooter: false,
        type: 'main',
      }
    )

    flowCtrl.insertAfter(
      RegistrationStepKey.CREATE_ACCOUNT,
      RegistrationStepKey.CREATE_WORKSPACE,
      {
        component: <SignupCreateWorkspaceStep />,
        hasFooter: false,
        type: 'main',
      }
    )

    // No dependencies so it runs only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Leave the signup flow only once /user/me resolves a real platform user — a 2xx with an
  // id-less stub/empty body must not redirect, mirroring the id-gated isUserSet (ISSUE-2266)
  useLayoutEffect(() => {
    if (isSuccess && provisionedUser?.id) window.location.href = Path.INDEX
  }, [isSuccess, provisionedUser])

  // Keep polling until the provisioned user materializes. The previous retry-on-error effect
  // was one-shot: its deps never changed on subsequent failed refetches, so polling silently
  // stopped after the first retry
  useEffect(() => {
    if (!isUserCreated || provisionedUser?.id) return
    const timer = window.setInterval(() => refetch(), 2000)
    return () => window.clearInterval(timer)
  }, [isUserCreated, provisionedUser, refetch])

  return (
    <Stack
      height="100vh"
      justifyContent="flex-start"
      bgcolor={BEIGE_300}
      overflow="hidden"
      paddingTop="6.4rem"
      position="relative"
    >

      {!isMobileView &&
        <Stack
          padding="2rem 0"
          gap={2}
          justifyContent="center"
          alignItems="center"
          height="6.8rem"
          position="fixed"
          width="100%"
          sx={{ background: BEIGE_300 }}
        >
          <Box minWidth="50rem">
            <DynamicFlowStepper stepNameTranslatorFnc={(key) => t(`steps.${key}`)} />
          </Box>
        </Stack>
      }

      {isMobileView &&
        <Stack
          gap=".4rem"
          padding="2.4rem 1.6rem"
          flexDirection="row"
          position="fixed"
          top="6.4rem"
          width="100%"
          boxSizing="border-box"
          sx={{ background: BEIGE_300 }}
        >
          {Object.keys(RegistrationStepKey).map((key, index) => {
            const nodeList = flowCtrl.getOrderedNodeList()
            const currentStep = flowCtrl.currentNode
            const currentStepIndex = currentStep ? nodeList.findIndex((node) => node.key === currentStep.key) : 0

            return <Box key={key} width="100%" height='.4rem' borderRadius=".4rem" sx={{ background: index <= currentStepIndex ? BLUE_600 : BEIGE_700 }} />
          })}
        </Stack>
      }

      <Box
        flex="1 1 100%"
        width="100%"
        height="100%"
        overflow="auto"
      >
        <Stack
          width="100%"
          height="100%"
          alignItems="center"
          padding={isMobileView ? '0' : '8rem 4rem'}
          boxSizing="border-box"
        >
          <DynamicFlowView />
        </Stack>
      </Box>

    </Stack>
  )
}
